'use client';

/**
 * PinnedCourseCompact — mobile's ~150px course pin (design turn 14a),
 * combining the compact anatomy with 14b's swipe (when the pin holds >1
 * course) and tap-to-expand. Owns its own item-`index` state — the caller
 * mounts this keyed by the pin's id (see pinned-card-mobile.tsx) so a fresh
 * pin always starts its own carousel at 0 rather than inheriting a stale
 * index from whatever pin was front before it.
 *
 * Tap-to-expand: tapping the card body (never the Register CTA) opens the
 * SAME full `CourseCardFull` in a bottom Sheet — the T12a mobile pattern
 * (see call-controls.tsx's mic-switcher sheet, which this borrows its grab-
 * handle/rounded-top chrome from verbatim). "Nothing is lost, just
 * deferred" — the compact body is a summary, not a replacement, of the full
 * card.
 */
import { memo, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import type { Course } from '@/types/course';
import { PinnedCardShell } from '../primitives/pinned-card-shell';
import { PinHeaderNav } from '../primitives/pin-header-nav';
import { SwipeTrack } from '../primitives/swipe-track';
import { RegisterCTA } from '../primitives/register-cta';
import { CourseCardFull } from '../content/course-card-full';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  fmtDateRange,
  fmtWeekdaySpan,
  RANGE_SEPARATOR_TIGHT,
} from '@/lib/course-date-format';

/** "Fri–Sun, 17–19 Jul · Fremont, CA" — day-range + date-range, then city.
 *  Either half drops out (never blank-padded) when the course is missing
 *  the corresponding fields. Compact-specific composition — stays local
 *  rather than moving into course-date-format.ts, unlike the two shared
 *  formatters it calls. Uses the TIGHT (unspaced) en-dash to fit this
 *  card's narrower width, unlike course-card-full.tsx/course-row.tsx's
 *  spaced default. */
function fmtCompactMeta(course: Course): string | null {
  const { eventStartDate, eventEndDate, city, locationCity, state } = course;
  const parts: string[] = [];
  if (eventStartDate) {
    parts.push(
      `${fmtWeekdaySpan(eventStartDate, eventEndDate, RANGE_SEPARATOR_TIGHT)}, ${fmtDateRange(eventStartDate, eventEndDate, RANGE_SEPARATOR_TIGHT)}`
    );
  }
  const displayCity = city || locationCity;
  if (displayCity) {
    parts.push(state ? `${displayCity}, ${state}` : displayCity);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

// Memoized: SwipeTrack's drag state is local to itself (see swipe-track.tsx),
// so `course`/`onOpenDetail` stay referentially stable while dragging — memo
// skips re-rendering (and re-running fmtCompactMeta's Intl date calls) on
// every pointermove of the track.
const CourseCompactBody = memo(function CourseCompactBody({
  course,
  onOpenDetail,
}: {
  course: Course;
  onOpenDetail: () => void;
}) {
  const meta = fmtCompactMeta(course);
  const registerHref = course.url || '#';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetail();
        }
      }}
      className="flex cursor-pointer flex-col gap-[7px] rounded-[13px] border-[1px] border-[#f0e7da] bg-white p-[12px] px-[13px]"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="min-w-0 truncate font-[family-name:var(--font-lora)] text-[16px] font-semibold leading-snug text-[#31364E]">
          {course.title}
        </h3>
        <span className="shrink-0 font-[family-name:var(--font-lora)] text-[16px] font-bold text-[#E36001]">
          {course.unitPrice != null ? `$${course.unitPrice}` : 'Free'}
        </span>
      </div>
      {meta && (
        <div className="flex items-center gap-1.5 text-[12.5px] text-[#8a7c68]">
          <CalendarDays
            className="size-[13px] shrink-0 text-[#E36001]"
            strokeWidth={2}
          />
          <span className="truncate">{meta}</span>
        </div>
      )}
      {/* Register must escape both the plain single-course onClick above AND
          SwipeTrack's pointer-gesture tracking in the multi-course case — a
          tap here should only navigate, never also pop the detail sheet. */}
      <div
        className="mt-0.5"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onPointerCancel={(e) => e.stopPropagation()}
      >
        <RegisterCTA href={registerHref} size={40} fullWidth>
          Register now
        </RegisterCTA>
      </div>
    </div>
  );
});

export interface PinnedCourseCompactProps {
  courses: Course[];
  onDismiss: () => void;
  /** "Pinned by Sara" (lone pin) or the stack's kind-based label (14c) —
   *  see pin-history.ts's frontBadgeLabel. */
  badgeLabel: string;
}

export function PinnedCourseCompact({
  courses,
  onDismiss,
  badgeLabel,
}: PinnedCourseCompactProps) {
  const [index, setIndex] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const clampedIndex = Math.min(index, courses.length - 1);

  const openDetail = () => setSheetOpen(true);

  return (
    <>
      <PinnedCardShell
        compact
        onDismiss={onDismiss}
        badgeLabel={badgeLabel}
        headerNav={
          courses.length > 1 ? (
            <PinHeaderNav
              index={clampedIndex}
              count={courses.length}
              onPrev={() => setIndex((i) => Math.max(0, i - 1))}
              onNext={() =>
                setIndex((i) => Math.min(courses.length - 1, i + 1))
              }
            />
          ) : undefined
        }
      >
        {courses.length === 1 ? (
          <CourseCompactBody course={courses[0]} onOpenDetail={openDetail} />
        ) : (
          <SwipeTrack
            items={courses}
            index={clampedIndex}
            onIndexChange={setIndex}
            renderItem={(course) => (
              <CourseCompactBody course={course} onOpenDetail={openDetail} />
            )}
            onTap={openDetail}
          />
        )}
      </PinnedCardShell>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          aria-describedby={undefined}
          // Same T12a mobile-panel geometry as call-controls.tsx's mic
          // switcher sheet — grab handle + rounded-top warm surface.
          className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-[26px] border-t-0 bg-[#FFFAF5] px-[12px] pb-[24px] pt-[8px]"
          style={{ boxShadow: '0 -20px 44px -18px rgba(49,54,78,.4)' }}
        >
          <div
            className="mx-auto mb-[10px] h-[4px] w-[38px] shrink-0 rounded-[3px] bg-[#e6dccd]"
            aria-hidden="true"
          />
          {/* Visually redundant with the card's own title — kept for the
              Sheet's required accessible name (Radix warns without one). */}
          <SheetTitle className="sr-only">Course details</SheetTitle>
          <CourseCardFull course={courses[clampedIndex]} />
        </SheetContent>
      </Sheet>
    </>
  );
}

export default PinnedCourseCompact;
